import React, {
  ChangeEvent, FC, FormEvent, useEffect, useState
} from 'react';
import {
  useTranslation
} from 'react-i18next';

import {
  Button,
  Input,
  ModalContainer,
  Spinner,
  errorToast,
  successfulToast,
} from 'src/shared/components';
import {
  showWarningFromServer
} from 'src/shared/utils';
import {
  usePatchOrgLoginCoverMutation,
  usePostOrgRequestSubdomainMutation,
} from 'src/shared/api/organizations/organizationsApi';

import * as Style from '../EditEntityByAdmin';

const MAX_COVER_SIZE_MB = 4;
const MAX_COVER_SIZE_BYTES = MAX_COVER_SIZE_MB * 1024 * 1024;

interface SetupCustomLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  currentSubdomain?: string | null;
  currentCoverPhotoUrl?: string | null;
  pendingSubdomain?: string | null;
}

export const SetupCustomLoginModal: FC<SetupCustomLoginModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  currentSubdomain,
  currentCoverPhotoUrl,
  pendingSubdomain,
}) => {
  const {
    t
  } = useTranslation();

  const [subdomain, setSubdomain] = useState(currentSubdomain || '');

  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentCoverPhotoUrl || null,
  );

  const [pendingFile, setPendingFile] = useState<File | null>(null);

  useEffect(
    () => {
      if (isOpen && currentCoverPhotoUrl) {
        setPreviewUrl(currentCoverPhotoUrl);
        setPendingFile(null);
      }
    },
    [isOpen, currentCoverPhotoUrl]
  );

  const [requestSubdomain, {
    isLoading: isSubdomainLoading
  }] = usePostOrgRequestSubdomainMutation();

  const [uploadLoginCover, {
    isLoading: isCoverLoading
  }] = usePatchOrgLoginCoverMutation();

  const handleFile = (file: File) => {
    if (!/^image\/(png|jpeg|jpg)$/i.test(file.type)) {
      errorToast(t('errors.selectValidImgFile'));

      return;
    }

    if (file.size > MAX_COVER_SIZE_BYTES) {
      errorToast(`${t('errors.selectImgFileLessThen')} ${MAX_COVER_SIZE_MB}Mb`);

      return;
    }

    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      handleFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();

    const file = event.dataTransfer.files?.[0];

    if (file) {
      handleFile(file);
    }
  };

  const handleSubdomainSubmit = async (): Promise<boolean> => {
    if (!subdomain.trim()) {
      return true;
    }

    try {
      const response = await requestSubdomain({
        id: organizationId,
        subdomain: subdomain.trim(),
      }).unwrap();

      successfulToast(response.message);

      return true;
    } catch (error) {
      showWarningFromServer(error);

      return false;
    }
  };

  const handleCoverUpload = async (): Promise<boolean> => {
    if (!pendingFile) {
      return true;
    }

    const formData = new FormData();

    formData.append(
      'file',
      pendingFile,
      pendingFile.name
    );

    try {
      const response = await uploadLoginCover({
        id: organizationId,
        formData,
      }).unwrap();

      successfulToast(response.message);
      setPendingFile(null);

      return true;
    } catch (error) {
      showWarningFromServer(error);

      return false;
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    let success = true;

    if (subdomain.trim() && subdomain.trim() !== currentSubdomain) {
      const ok = await handleSubdomainSubmit();

      if (!ok) {
        success = false;
      }
    }

    if (pendingFile) {
      const ok = await handleCoverUpload();

      if (!ok) {
        success = false;
      }
    }

    if (success) {
      onClose();
    }
  };

  const isLoading = isSubdomainLoading || isCoverLoading;

  return (
    <ModalContainer
      isOpen={isOpen}
      closeModalHandler={onClose}
      modalTitle={t('customLogin.setupCustomLogin')}
    >
      <Style.ContentContainer onSubmit={handleSubmit}>
        {/* Subdomain Input */}
        <div>
          <Input
            value={subdomain}
            onChange={(value) => setSubdomain(value)}
            placeholder="my-org"
            label={t('customLogin.subdomain')}
            disabled={!!currentSubdomain}
          />

          <p
            className="text-13-18 mt-1"
            style={{
              color: '#4e81f7',
            }}
          >
            {t('customLogin.subdomainNote')}
          </p>

          {pendingSubdomain && (
            <p className="text-13-18 text-grey-dark-40 mt-1">
              {t('customLogin.pendingSubdomain')}

              :
              {' '}

              <strong>{pendingSubdomain}</strong>
            </p>
          )}
        </div>

        {/* Cover Photo Upload */}
        <label
          htmlFor="coverPhotoInput"
          className="flex flex-col items-center justify-center cursor-pointer rounded py-2 px-4"
          style={{
            background: '#f4f7fe',
            border: '1px dashed #d3dffd',
          }}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <p className="text-13-21 text-dark-gray">
            {t('customLogin.dragAndDrop')}
          </p>

          <p
            className="text-13-21 font-medium"
            style={{
              color: '#2261f5',
            }}
          >
            {t('customLogin.clickToUpload')}
          </p>

          <p
            className="text-11-19 text-center"
            style={{
              color: '#717171',
            }}
          >
            {t('customLogin.coverPhotoRecommended')}
          </p>

          <input
            id="coverPhotoInput"
            type="file"
            accept=".jpg,.jpeg,.png"
            className="hidden"
            onChange={handleFileChange}
          />
        </label>

        {/* Mini Preview */}
        <div className="flex flex-col gap-2">
          <div>
            <p className="text-16-28 font-medium text-dark-gray">
              {t('customLogin.miniPreview')}
            </p>

            <p
              className="text-13-21"
              style={{
                color: '#717171',
              }}
            >
              {t('customLogin.miniPreviewSubtitle')}
            </p>
          </div>

          <div
            className="flex overflow-hidden"
            style={{
              height: 324,
            }}
          >
            {/* Left half — cover photo */}
            <div
              className="flex-1 relative overflow-hidden rounded-l-lg"
              style={{
                border: '1px solid #d3dffd',
                borderRight: 'none',
                boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.05)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundImage: previewUrl ? `url(${previewUrl})` : undefined,
                backgroundColor: previewUrl ? undefined : '#f4f7fe',
              }}
            />

            {/* Right half — login form mockup */}
            <div
              className="flex-1 relative overflow-hidden rounded-r-lg flex flex-col items-center justify-center gap-3 p-5"
              style={{
                border: '1px solid #d3dffd',
                borderLeft: 'none',
                boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.05)',
              }}
            >
              <div className="w-full flex flex-col gap-1">
                <p
                  className="text-11-19"
                  style={{
                    color: '#717171',
                  }}
                >
                  Email or username
                </p>

                <div
                  className="w-full rounded h-7"
                  style={{
                    border: '1px solid #d3dffd',
                    background: '#f4f7fe',
                  }}
                />
              </div>

              <div className="w-full flex flex-col gap-1">
                <p
                  className="text-11-19"
                  style={{
                    color: '#717171',
                  }}
                >
                  Password
                </p>

                <div
                  className="w-full rounded h-7"
                  style={{
                    border: '1px solid #d3dffd',
                    background: '#f4f7fe',
                  }}
                />
              </div>

              <div
                className="w-full rounded-full h-8 flex items-center justify-center"
                style={{
                  background: '#2261f5',
                }}
              >
                <span
                  className="text-11-19 font-bold"
                  style={{
                    color: 'white',
                  }}
                >
                  Log in
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="big-grey-bordered"
            className="font-bold text-xl w-max"
            onClick={onClose}
          >
            {t('buttons.cancel')}
          </Button>

          <Button
            variant="big-blue"
            className="font-bold text-xl w-max"
            type="submit"
            disabled={isLoading}
          >
            {t('buttons.saveApply')}

            {isLoading && (
              <Spinner
                color="white"
                size={24}
                centered={false}
              />
            )}
          </Button>
        </div>
      </Style.ContentContainer>
    </ModalContainer>
  );
};
